import { useState, useContext, useRef } from 'react'
import { timelineService } from '@/services/timeline';
import { TimelinePost } from '@/types/post';
import { useToast } from '@/hooks/use-toast'
import { TimelineContext } from '@/contexts/timeline'
import { CurationContext } from '@/contexts/curation'

export interface PostActions {
  isApproving: boolean
  isRejecting: boolean
  isPinning: boolean
  isFavoriting: boolean
  handleApprove: () => Promise<void>
  handleReject: () => Promise<void>
  handlePin: () => Promise<void>
  handleFavorite: () => Promise<void>
}

export function usePostActions(post: TimelinePost): PostActions {
  const [isApproving, setIsApproving] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [isPinning, setIsPinning] = useState(false)
  const [isFavoriting, setIsFavoriting] = useState(false)
  const { toast } = useToast()
  const timelineContext = useContext(TimelineContext)
  const curationContext = useContext(CurationContext)

  const removePost = (postId: string) => {
    if (timelineContext) {
      timelineContext.removePost(postId)
    } else if (curationContext) {
      curationContext.removePost(postId)
    }
  }

  const updatePost = (postId: string, changes: any) => {
    if (timelineContext) {
      timelineContext.updatePost(postId, changes)
    } else if (curationContext) {
      curationContext.updatePost(postId, changes)
    }
  }

  const handleApprove = async () => {
    setIsApproving(true)
    try {
      await timelineService.approve(post.id)
      removePost(post.id)
      toast({
        title: 'Post aprovado',
        description: 'O post foi aprovado com sucesso.',
      })
    } catch {
      toast({
        title: 'Erro ao aprovar',
        description: 'Não foi possível aprovar o post. Tente novamente.',
        variant: 'error',
      })
    } finally {
      setIsApproving(false)
    }
  }

  const handleReject = async () => {
    setIsRejecting(true)
    try {
      await timelineService.reject(post.id)
      removePost(post.id)
      toast({
        title: 'Post rejeitado',
        description: 'O post foi rejeitado com sucesso.',
      })
    } catch {
      toast({
        title: 'Erro ao rejeitar',
        description: 'Não foi possível rejeitar o post. Tente novamente.',
        variant: 'error',
      })
    } finally {
      setIsRejecting(false)
    }
  }

  const handlePin = async () => {
    setIsPinning(true)
    try {
      await timelineService.pin(post.id)
      updatePost(post.id, { is_pinned: !post.is_pinned })
      toast({
        title: post.is_pinned ? 'Post desfixado' : 'Post fixado',
        description: post.is_pinned ? 'O post foi desfixado com sucesso.' : 'O post foi fixado com sucesso.',
      })
    } catch {
      toast({
        title: 'Erro ao fixar post',
        description: 'Não foi possível fixar o post. Tente novamente.',
        variant: 'error',
      })
    } finally {
      setIsPinning(false)
    }
  }

  const previousFavoriteStateRef = useRef<boolean | null>(null)

  const handleFavorite = async () => {
    if (isFavoriting) return

    setIsFavoriting(true)
    const currentFavoriteState = post.is_favorited ?? false
    const newFavoriteState = !currentFavoriteState

    previousFavoriteStateRef.current = currentFavoriteState

    updatePost(post.id, { is_favorited: newFavoriteState })

    try {
      await timelineService.favorite(post.id)
      toast({
        title: newFavoriteState ? 'Adicionado aos favoritos' : 'Removido dos favoritos',
        description: newFavoriteState ? 'O post foi adicionado aos favoritos.' : 'O post foi removido dos favoritos.',
      })
      previousFavoriteStateRef.current = null
    } catch (error) {
      const previousState = previousFavoriteStateRef.current ?? currentFavoriteState
      updatePost(post.id, { is_favorited: previousState })
      previousFavoriteStateRef.current = null
      toast({
        title: 'Erro ao favoritar post',
        description: 'Não foi possível favoritar o post. Tente novamente.',
        variant: 'error',
      })
    } finally {
      setIsFavoriting(false)
    }
  }

  return {
    isApproving,
    isRejecting,
    isPinning,
    isFavoriting,
    handleApprove,
    handleReject,
    handlePin,
    handleFavorite,
  }
}

